/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import type { DataGridColumnV1 } from "@startree-ui/platform-ui";
import axios from "axios";
import { isEmpty } from "lodash";
import type { DataTableRecords } from "../../components/dataset-ingestion-flow/dataset-ingestion-flow.interfaces";
import {
    CreateDatasetArgsType,
    IngestionFormats,
} from "../../rest/dto/datasets.interfaces";
import {
    CategoryTypes,
    ConnectionConfig,
    ConnectionTypes,
    InferConfigArgsType,
    InferSchemaArgsType,
    IngestionLocator,
    PreviewIngestionArgsType,
    PreviewIngestionResponseType,
    RunIngestionArgsType,
} from "../../rest/dto/datasources.interfaces";
import {
    Config,
    ConnectionState,
    DatasetConfig,
    OutputSchema,
    TableConfig,
    TableTypes,
} from "../../store/dataset-ingestion/dataset-ingestion.interfaces";
import { getLocatorValue } from "../review-and-submit/review-and-submit-util";
import { escapeSpecialChars, isValidJSONString } from "../utils";
import type {
    GetCreateDatasetArgs,
    GetInferConfigArgs,
    GetInferSchemaArgs,
    GetPreviewIngestionArgs,
    GetRunIngestionArgs,
} from "./dataset-ingestion-utils.interfaces";

// config utils

// Remove all invalid index configs
export const removeInvalidStartreeIndexConfigs = (
    config: TableConfig | undefined
): TableConfig | undefined => {
    if (!config) {
        return config;
    }

    const { starTreeIndexConfigs } = config.tableIndexConfig;

    if (!starTreeIndexConfigs) {
        return config;
    }

    // Each of these two properties should be filled for the index to be valid
    const prunedConfig = starTreeIndexConfigs.filter(
        (t) =>
            t.dimensionsSplitOrder.length > 0 &&
            (t.functionColumnPairs || []).length > 0
    );

    config.tableIndexConfig.starTreeIndexConfigs = prunedConfig;

    return config;
};

export const getCombinedConfig = (
    tableConfig: Config
): Record<string, TableConfig> => {
    const offlineConfig = removeInvalidStartreeIndexConfigs(
        tableConfig.offline?.config
    );
    const realtimeConfig = removeInvalidStartreeIndexConfigs(
        tableConfig.realtime?.config
    );

    return {
        ...(offlineConfig && { offline: offlineConfig }),
        ...(realtimeConfig && { realtime: realtimeConfig }),
    };
};

/**
 * Only `Streaming` connections contain REALTIME data tables
 * All other connections create OFFLINE data tables
 */
export const getConfigStr = (
    datasetName: string,
    schema: Partial<OutputSchema["schema"]> | OutputSchema["schema"]["schema"],
    tableConfig: Config
): string => {
    const { offline, realtime } = getCombinedConfig(tableConfig);

    const objConfig = {
        offline,
        realtime,
        schema,
        tableName: datasetName,
    };

    return JSON.stringify(objConfig);
};

export const getConfigStrWithoutMappings = (
    datasetName: string,
    schema: OutputSchema["schema"],
    tableConfig: Config
): string => {
    // take only schema
    const schemaWithoutMappings = schema.schema;

    return getConfigStr(datasetName, schemaWithoutMappings, tableConfig);
};

/**
 * keys with a dot `.` tend to not work as preview table keys.
 * Replace them with an underscore `_` */
export const getSanitizedKey = (key: string): string => key.replace(/\./g, "_");

// ingestion preview utils
export const convertPreviewColumnsToDataGridColumns = (
    columnNames: PreviewIngestionResponseType["columnNames"],
    minWidth = 180
): DataGridColumnV1<DataTableRecords>[] =>
    columnNames.map((columnName) => ({
        key: columnName,
        dataKey: getSanitizedKey(columnName),
        header: columnName,
        minWidth,
    }));

export const convertPreviewRecordToDataGridData = (
    previewIngestion: PreviewIngestionResponseType
): DataTableRecords[] =>
    previewIngestion.records?.map((record, index) =>
        previewIngestion.columnNames.reduce((prev, key, currentIndex) => {
            return {
                ...prev,
                // while searching defaultDataGridUniqueId is unique identifier for
                // data grid to distinguish between rows
                previewId: index,
                [getSanitizedKey(key)]:
                    typeof record[currentIndex] === "string"
                        ? record[currentIndex]
                        : JSON.stringify(record[currentIndex]).replace(
                              /\\"/g,
                              "'"
                          ),
            };
        }, {} as DataTableRecords)
    );

// API utils
export const getInferConfigArgs = ({
    connection,
    dataset,
    inputSchema,
    isHybridConfig = false,
    outputSchema,
    timeColumn,
}: GetInferConfigArgs): InferConfigArgsType => {
    const { type: connectionType, category: connectionCategory } = connection;

    // Base payload
    const inferConfigArgs = {
        dataSetName: dataset.name,
        ingestionFormat: dataset.ingestionFormat as IngestionFormats,
        ingestionLocator: dataset.ingestionLocator as IngestionLocator,
        ingestionType: connectionType as ConnectionTypes,
        outputSchema: {
            ...outputSchema,
            schema: escapeSpecialChars(JSON.stringify(outputSchema.schema)),
        },
        ...(timeColumn &&
            !isEmpty(timeColumn) && { timeColumn: timeColumn.name }),
        isDimensionTable: dataset.tableType === TableTypes.DIMENSION,
    } as InferConfigArgsType;

    const ingestionConnection = connection.properties;

    // all done for Upload dataset config requirements
    if (connectionType === ConnectionTypes.UPLOAD) {
        return inferConfigArgs;
    }

    // Special cases for Kafka
    if (connectionType === ConnectionTypes.KAFKA) {
        const isAvro = dataset.ingestionFormat === IngestionFormats.AVRO;

        if (ingestionConnection && isAvro) {
            ingestionConnection["stream.kafka.decoder.prop.schema"] =
                JSON.stringify(inputSchema.schema);
        }
    }

    // Add primary keys if the connection category is streaming
    if (connectionCategory === CategoryTypes.STREAMING) {
        const primaryKeyColumns =
            outputSchema.schema.schema.primaryKeyColumns || [];

        if (!isEmpty(primaryKeyColumns) && !isHybridConfig) {
            inferConfigArgs.datasetProperties = {
                "kafka.upsert.primaryKeyColumns.group": {
                    "kafka.upsert.primaryKeyColumns":
                        primaryKeyColumns.join(","),
                },
            };
        }
    }

    if (ingestionConnection) {
        inferConfigArgs.ingestionConnection = ingestionConnection;
    }

    // Pretend the connection type is pinot for hybrid tables
    if (isHybridConfig) {
        inferConfigArgs.ingestionType = ConnectionTypes.PINOT;
    }

    return inferConfigArgs;
};

export const getInferSchemaArgs = ({
    connectionProperties,
    connectionType,
    dataset,
    inputSchema,
}: GetInferSchemaArgs): InferSchemaArgsType => {
    const { ingestionFormat, ingestionLocator } = dataset;

    // set format & locator
    const format = ingestionFormat || IngestionFormats.UNKNOWN;
    const locator = ingestionLocator || undefined;

    // if upload, nothing more is needed
    if (connectionType === ConnectionTypes.UPLOAD) {
        return {
            ingestionFormat: format,
            ingestionLocator: locator,
            ingestionType: connectionType,
        };
    }

    // apply any current manual changes to connection properties
    let ingestionConnection = connectionProperties as ConnectionConfig;
    if (
        connectionType === ConnectionTypes.KAFKA &&
        ingestionFormat === IngestionFormats.AVRO &&
        inputSchema
    ) {
        const inputSchemaIsString = typeof inputSchema === "string";
        const inputSchemaArg = inputSchemaIsString
            ? inputSchema
            : JSON.stringify(inputSchema);

        if (inputSchemaArg && isValidJSONString(inputSchemaArg)) {
            ingestionConnection = Object.assign({}, ingestionConnection, {
                "stream.kafka.decoder.prop.schema": inputSchemaArg,
            });
        }
    }

    return {
        ingestionConnection,
        ingestionFormat: format,
        ingestionLocator: locator,
        ingestionType: connectionType,
    };
};

export const getPreviewIngestionArgs = ({
    connection,
    dataset,
    outputSchema,
    previewIngestion,
}: GetPreviewIngestionArgs): PreviewIngestionArgsType => {
    const { properties, type } = connection;
    const { ingestionFormat, ingestionLocator, name, timeColumn } = dataset;
    const { columnNames = [], records = [] } = previewIngestion || {};

    // using input records to preview ingestion
    let inputRecords;
    if (!isEmpty(columnNames) && !isEmpty(records)) {
        inputRecords = {
            columnNames,
            records,
        };
    }

    return {
        ingestionConnection: properties || undefined,
        ingestionFormat: ingestionFormat as IngestionFormats,
        ingestionLocator: ingestionLocator as IngestionLocator,
        ingestionType: type as ConnectionTypes,
        dataSetName: name,
        inputRecords,
        ...(outputSchema && {
            outputSchema: {
                ...outputSchema,
                schema: JSON.stringify(outputSchema?.schema),
            },
        }),
        timeColumn: timeColumn?.name,
    };
};

// SAMPLE types won't have Config or Schema
// UPLOAD types won't have Connection values
export const getCreateDatasetArgs = ({
    config,
    connection,
    dataset,
    pinotConnection,
    inputSchema,
    outputSchema,
    sampleDatasetId,
}: GetCreateDatasetArgs): CreateDatasetArgsType => {
    const { srn: connectionSrn, type } = connection as {
        srn: string;
        type: ConnectionTypes;
    } & ConnectionState;
    const { description, ingestionFormat, name, namespace } = dataset;
    const pinotSrn = pinotConnection?.srn || ""; // There is no connection srn for sample datasets

    const isSample = type === ConnectionTypes.SAMPLE && sampleDatasetId;

    let newDatasetConfig = {
        dataSourceSrn: pinotSrn,
        description,
        namespaceSrn: namespace.srn,
        name,
    };

    // SAMPLE requirements met
    if (isSample && sampleDatasetId) {
        return Object.assign({}, newDatasetConfig, {
            sampleId: sampleDatasetId,
        });
    }

    // apply next set of requirements
    newDatasetConfig = Object.assign({}, newDatasetConfig, {
        ingestionFormat,
        ingestionSourceSchema:
            (!!inputSchema && {
                ...inputSchema,
                ...(typeof inputSchema.schema === "string"
                    ? { schema: inputSchema.schema }
                    : {
                          schema: JSON.stringify(inputSchema.schema),
                      }),
            }) ||
            {},
        ingestionType: type,
    });

    // can't proceed, return what we have - will error
    if (!config || !outputSchema) {
        return newDatasetConfig;
    }

    // const isNewConnection = !currentConnection && !isUpload && !isSample;
    const isUpload = type === ConnectionTypes.UPLOAD;
    const isRealtime = (config && Boolean(config.realtime)) || false;
    // const isHybrid = (isRealtime && config && Boolean(config.offline)) || false;

    const configStr = getConfigStrWithoutMappings(
        // isDefaultNamespace ? name : `${namespace.name}.${name}`,
        name,
        outputSchema.schema,
        config
    );
    const selectedConfig =
        (isRealtime && (config as Config).realtime) ||
        (config as Config).offline;

    // apply config requirements
    newDatasetConfig = Object.assign({}, newDatasetConfig, {
        configStr,
        dataSetType: (selectedConfig as DatasetConfig).type,
    });

    const locatorValue = getLocatorValue(type, dataset);

    // UPLOAD requirements met
    if (isUpload) {
        return { ...newDatasetConfig, ingestionLocator: locatorValue };
    }

    // apply Connector-related requirements
    newDatasetConfig = Object.assign({}, newDatasetConfig, {
        ingestionDataSourceSrn: connectionSrn,
        ingestionLocator: locatorValue,
    });

    // ALL OTHER requirements met
    return newDatasetConfig;
};

export const getRunIngestionArgs = ({
    connection,
    dataset,
    pinotConnection,
}: GetRunIngestionArgs): RunIngestionArgsType => {
    const { type } = connection;
    const { ingestionFormat, ingestionLocator, name } = dataset;
    const { srn } = pinotConnection;

    return {
        dataSourceSrn: srn,
        datasetName: name,
        ingestionFormat: ingestionFormat || undefined,
        ingestionType: type as ConnectionTypes,
        payloadBase64: ingestionLocator?.["upload.payloadBase64"] || "",
        ...(ingestionLocator?.recordReaderConfig && {
            recordReaderConfig: ingestionLocator?.recordReaderConfig,
        }),
    };
};

const PREVIEW_TABLE_API_CACHE_KEY = "is-preview-table-api-enabled";

export const shouldUsePreviewTableAPI = (
    connectionType: ConnectionState["type"]
): boolean => {
    const cachedValue = localStorage.getItem(PREVIEW_TABLE_API_CACHE_KEY);

    if (cachedValue === "true") {
        return (
            connectionType === ConnectionTypes.S3 ||
            connectionType === ConnectionTypes.KAFKA
        );
    }

    return false;
};

export const fetchPreviewTableAPIFT = async (): Promise<void> => {
    try {
        const response = await axios.get(
            "/api/v1/data-manager/pinot/preview/enabled"
        );
        const data = response.data;

        localStorage.setItem(PREVIEW_TABLE_API_CACHE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error("Error fetching preview table API status:", error);
    }
};

const DATAPORTAL_API_CACHE_KEY = "is-dataportal-api-enabled";

export const shouldRedirectUserToDP = (): boolean => {
    const cachedValue = localStorage.getItem(DATAPORTAL_API_CACHE_KEY);

    return cachedValue === "true";
};

export const fetchDataportalAPIFT = async (): Promise<void> => {
    try {
        const response = await axios.get(
            "/api/v1/data-manager/dataportal/enabled"
        );
        const data = response.data;

        localStorage.setItem(DATAPORTAL_API_CACHE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error("Error fetching dataportal API status:", error);
    }
};
